'use client'

import { Skeleton } from '@/core/ui/atoms/skeleton'
import {
  PayPalButtons,
  PayPalButtonsComponentProps,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js'
import { usePaymentStore } from '../store'

interface PaypalPaymentButtonProps extends PayPalButtonsComponentProps {
  amount: string | number
  onPaymentSuccess?: (data: Record<string, unknown>) => void
  onPaymentError?: (message: string) => void
  onPaymentCancel?: (data: Record<string, unknown>) => void
  currency?: string
  transactionId?: string
}

export const PaypalPaymentButton: React.FC<PaypalPaymentButtonProps> = ({
  amount,
  onPaymentSuccess,
  onPaymentError,
  onPaymentCancel,
  currency,
  transactionId,
  ...props
}) => {
  const [{ isPending }] = usePayPalScriptReducer()
  const { setError, reset } = usePaymentStore()

  const locale = 'en_US' // TODO add locale
  const currency_code = currency || 'USD'

  return (
    <>
      {isPending && (
        <div className='flex items-center justify-center'>
          <Skeleton width={100} height={44} />
        </div>
      )}

      <PayPalButtons
        style={{
          layout: 'horizontal',
          shape: 'rect',
          disableMaxWidth: true,
          tagline: false,
          color: 'blue',
          label: 'paypal',
          height: 44,
        }}
        className='z-10 h-11'
        createOrder={(_, actions) => {
          reset()
          return actions.order.create({
            intent: 'CAPTURE',
            purchase_units: [
              {
                invoice_id: transactionId,
                amount: {
                  value: amount.toString(),
                  currency_code,
                  breakdown: {
                    item_total: { value: amount.toString(), currency_code },
                    shipping: { value: '0', currency_code },
                    shipping_discount: { value: '0', currency_code },
                  },
                },
              },
            ],
            application_context: { locale: locale.replace('_', '-') },
          })
        }}
        onApprove={async (_, actions) => {
          const details = await actions.order!.capture()
          onPaymentSuccess?.(details)
        }}
        onError={error => {
          const message =
            'message' in error
              ? (error?.message as string)
              : 'PayPal payment failed'
          setError(message)
          onPaymentError?.(message)
        }}
        onCancel={data => {
          onPaymentCancel?.(data)
        }}
        {...props}
      />
    </>
  )
}
